import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Button, Card } from 'xland-ui';
import { Plus, Trash } from 'lucide-react';
import Popup from 'components/Popup';
import InputText from 'components/Inputs/inputText';
import InputSelect from 'components/Inputs/inputSelect';
import { useOrganizationEdit } from './hooks/useOrganizationEdit';

function PopupEditOrganization({ organization = {}, onClose, onSave }) {
  const literals = useSelector(state => state.i18n.literals.investmentAssets);
  const literalsCommon = useSelector(state => state.i18n.literals.common);

  const {
    loading,
    organizationData,
    setOrganizationData,
    handleMemberChange,
    handleAddMember,
    handleRemoveMember,
    handleSave,
  } = useOrganizationEdit(organization, onSave, onClose);

  const permissionOptions = [
    { id: 1, name: literalsCommon.edit },
    { id: 2, name: literalsCommon.view },
  ];

  const isEditing = Boolean(organizationData.id);

  return (
    <Popup
      title={isEditing ? literals.editOrganization : literals.createOrganization}
      onClose={onClose}
    >
      <InputText
        preText={literalsCommon.name}
        value={organizationData.name}
        onChange={(value) => setOrganizationData(prev => ({ ...prev, name: value }))}
      />

      <div className='members'>
        <div className='d-flex justify-content-between align-items-end'>
          <h6 className='fc-light'>{literals.members}</h6>
          <Button
            className='mb-2'
            icon={Plus}
            text={literals.inviteUser}
            onClick={handleAddMember}
          />
        </div>

        {!organizationData.members?.length ? (
          <Card className='mb-5'>
            <div className='text-center py-5'>
              <p className='fc-light mb-0'>{literals.noMembers}</p>
            </div>
          </Card>
        ) : (
          organizationData.members.map((member, index) => (
            <Card key={member.email} className='mb-3'>
              <div className='d-flex justify-content-between align-items-center'>
                <p className='fc-light mb-0'>{`${literals.member} ${index + 1}`}</p>
                <Trash size={18} onClick={() => handleRemoveMember(member.email)} className='pointer' />
              </div>
              <div className='row align-items-center my-2'>
                {!member.exist && (
                  <>
                    <div className='col-6'>
                      <InputText
                        className='mb-0'
                        placeholder={literalsCommon.name}
                        value={member.firstName || ''}
                        onBlur={(value) => handleMemberChange(member.email, 'firstName', value)}
                        state
                      />
                    </div>
                    <div className='col-6'>
                      <InputText
                        className='mb-0'
                        placeholder={literalsCommon.lastName}
                        value={member.lastName || ''}
                        onBlur={(value) => handleMemberChange(member.email, 'lastName', value)}
                        state
                      />
                    </div>
                  </>
                )}
                <div className='col-8'>
                  <InputText
                    className='mb-0'
                    placeholder={literalsCommon.email}
                    value={member.email}
                    onBlur={(value) => handleMemberChange(member.email, 'email', value)}
                    state
                  />
                </div>
                <div className='col-4'>
                  <InputSelect
                    className='mb-0'
                    options={permissionOptions}
                    value={member.permission}
                    onChange={(value) => handleMemberChange(member.email, 'permission', value)}
                  />
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <div className='buttons'>
        <Button
          text={literalsCommon.save}
          onClick={handleSave}
          loading={loading}
        />
      </div>
    </Popup>
  );
}

PopupEditOrganization.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    members: PropTypes.arrayOf(PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      permission: PropTypes.number.isRequired,
    })).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default PopupEditOrganization;
